/**
 * An attempt's words, as the engine, the sellers' drivers and the clients
 * share them: its phases and milestones, the total and vendor-code prompts
 * a buyer answers, a store's delivery option, and the buyer's name and
 * street address as one-field checkouts take them.
 */
import { z } from "zod";
import type { Buyer } from "@libs/identity-onboarding-contracts/buyer";

/** The buyer's street address in one line, for a checkout whose form has
 *  no second line of its own: the first line, then the second ("1 Example
 *  Street, Flat 2"). */
export const streetAddress = (buyer: Partial<Pick<Buyer, "address" | "address2">>, separator = ", "): string | undefined =>
  buyer.address ? [buyer.address, buyer.address2].filter(Boolean).join(separator) : undefined;

/** The buyer's name for a checkout with one name field (Skiddle's): their
 *  first and last name. An attempt started before they were asked for apart
 *  holds the whole name the buyer confirmed then, and only that. */
export const buyerFullName = (
  buyer: Pick<Buyer, "firstName" | "lastName" | "name">,
): string | undefined =>
  buyer.firstName && buyer.lastName
    ? `${buyer.firstName} ${buyer.lastName}`
    : buyer.name;

export const phaseSchema = z.enum([
  "queued",
  "starting_browser",
  "waiting_for_browser",
  "opening_vendor",
  "selecting_tickets",
  "checking_account",
  "human_check",
  "vendor_code",
  "signing_in",
  "registering",
  "filling_details",
  "filling_card",
  "checking_total",
  "confirming_total",
  "ready_for_payment",
  "paying",
  "bank_check",
  "bought",
  "bank_check_required",
  "payment_declined",
  "outcome_unknown",
  "cancelling",
  "cancelled",
  "failed",
  "needs_attention",
]);
export type Phase = z.infer<typeof phaseSchema>;
export const terminalPhases: readonly Phase[] = [
  "ready_for_payment",
  "bought",
  "bank_check_required",
  "payment_declined",
  "outcome_unknown",
  "cancelled",
  "failed",
  "needs_attention",
];
export type Milestone = { sequence: number; phase: Phase; at: string };
/** What the buyer confirms before Pay where the seller adds tax at checkout:
 *  the total its last page shows, the tax included, in the offer's
 *  currency, and whether they have answered. */
export type TotalPrompt = { totalMinor: number; taxMinor: number; answered: boolean };
/** The buyer's answer to a `confirming_total` prompt: pay that total, or
 *  stop before Pay. */
export const totalAnswerSchema = z
  .object({ id: z.uuid(), sequence: z.number().int().positive(), accept: z.boolean() })
  .strict();
export type TotalAnswer = z.infer<typeof totalAnswerSchema>;
/** The ways a vendor sends a sign-in code. */
export const vendorCodeChannels = ["sms", "email", "call"] as const;
export type VendorCodeChannel = (typeof vendorCodeChannels)[number];
/** What a vendor's code is for: signing in to the buyer's account (DICE's
 *  code, a check a vendor asks on a sign-in it doubts), or verifying the
 *  email or the mobile number of an account being made for the buyer
 *  (Ticketmaster's new account asks for both, one after the other), or
 *  confirming the buyer's mobile number for a payment (PayPal's check of a
 *  guest card payment). */
export const vendorCodePurposes = ["sign_in", "verify_email", "verify_phone", "confirm_payment"] as const;
export type VendorCodePurpose = (typeof vendorCodePurposes)[number];
/** Who sends a code that is not the seller's own: PayPal, whose guest card
 *  payment a seller's checkout takes (Ticket Tailor's, WooCommerce's). */
export const vendorCodeSenders = ["paypal"] as const;
export type VendorCodeSender = (typeof vendorCodeSenders)[number];
/** Where the vendor sent the buyer its code, as it shows it
 *  (`k****k@gmail.com`), how, and the other ways it can send one; `retry`
 *  when the last code given was refused. `answered` once the buyer has
 *  given one, until the vendor has read it. `purpose` is what the code is
 *  for; absent, signing in (an older server's prompts). `sender` is who
 *  sent it where that is not the seller; absent, the seller (an older
 *  server's prompts too). */
export type VendorCodePrompt = {
  sentTo: string | null;
  channel: VendorCodeChannel;
  channels: VendorCodeChannel[];
  retry: boolean;
  answered: boolean;
  purpose?: VendorCodePurpose;
  sender?: VendorCodeSender;
};
/** The buyer's answer to a `vendor_code` prompt: the code, or another way
 *  for the vendor to send one. */
export const vendorCodeAnswerSchema = z.union([
  z.object({ id: z.uuid(), sequence: z.number().int().positive(), code: z.string().trim().regex(/^\d{4,8}$/) }).strict(),
  z.object({ id: z.uuid(), sequence: z.number().int().positive(), resend: z.enum(vendorCodeChannels) }).strict(),
]);
export type VendorCodeAnswer = z.infer<typeof vendorCodeAnswerSchema>;
/** One delivery option the store offers for a basket, as its own estimate
 *  gives it: its code (the stable id its checkout's option carries), its
 *  name ("Amazon - Premium"), its fee, the dates it arrives between and the
 *  store's description; and `dutiesDue` where it leaves import duties to the
 *  fan (`leavesDutiesDue`; absent from an older server, and where its store
 *  doesn't say), which the sheet says beside it (LIS-1133). */
export const deliveryOptionSchema = z.object({
  code: z.string().min(1).max(200),
  name: z.string().min(1).max(240),
  feeMinor: z.number().int().nonnegative(),
  arrives: z.object({ from: z.iso.date(), to: z.iso.date() }).nullable(),
  description: z.string().max(500).nullable(),
  dutiesDue: z.literal(true).optional(),
});
export type DeliveryOption = z.infer<typeof deliveryOptionSchema>;

/** An uncertain remote stop (`needs_attention`) stays recoverable: it is not
 *  closed. A new purchase waits only on one whose fan has not seen its
 *  ending (`activeAttempt`). */
export const closedPhases: readonly Phase[] = [
  "ready_for_payment",
  "bought",
  "bank_check_required",
  "payment_declined",
  "outcome_unknown",
  "cancelled",
  "failed",
];
